import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { getCompiledTimecard } from '../../../services/apiService';

const TimecardRawData = ({ timecard }) => {
  const [copiedSection, setCopiedSection] = useState(null);
  const [compiled, setCompiled] = useState(null);
  const [compiledLoading, setCompiledLoading] = useState(true);
  const [compiledError, setCompiledError] = useState(null);

  useEffect(() => {
    const loadCompiled = async () => {
      if (timecard?.timecard_id || timecard?.id) {
        try {
          setCompiledLoading(true);
          const data = await getCompiledTimecard(timecard.timecard_id || timecard.id);
          setCompiled(data);
        } catch (err) {
          console.error('Failed to load compiled data:', err);
          setCompiledError(err?.response?.data?.detail || 'Not compiled yet');
        } finally {
          setCompiledLoading(false);
        }
      }
    };
    loadCompiled();
  }, [timecard]);

  const copyToClipboard = (data, section) => {
    navigator.clipboard.writeText(JSON.stringify(data, null, 2));
    setCopiedSection(section);
    setTimeout(() => setCopiedSection(null), 2000);
  };

  const parsed = timecard?.parsed_json || {};

  return (
    <div className="space-y-6">
      {/* Compiled JSON Section - NEW! */}
      <div className="card">
        <div className="p-4 border-b border-primary/10 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Icon name="Zap" className="w-5 h-5 text-primary" />
            <div>
              <h3 className="text-lg font-semibold text-foreground">Compiled Timecard JSON</h3>
              <p className="text-xs text-muted-foreground">Perfect Credit Hours (Phase 1)</p>
            </div>
          </div>
          {compiled && (
            <button
              onClick={() => copyToClipboard(compiled, 'compiled')}
              className="flex items-center gap-2 px-4 py-2 bg-primary/10 hover:bg-primary/20 text-primary rounded-lg transition-colors"
            >
              <Icon name={copiedSection === 'compiled' ? 'Check' : 'Copy'} className="w-4 h-4" />
              {copiedSection === 'compiled' ? 'Copied!' : 'Copy JSON'}
            </button>
          )}
        </div>
        <div className="p-4 bg-secondary/30">
          {compiledLoading ? (
            <div className="flex items-center justify-center py-8">
              <Icon name="Loader2" className="w-6 h-6 text-primary animate-spin" />
              <span className="ml-2 text-muted-foreground">Loading compiled data...</span>
            </div>
          ) : compiledError ? (
            <div className="flex items-center gap-2 py-4 text-muted-foreground">
              <Icon name="AlertCircle" className="w-5 h-5" />
              <span>{compiledError}</span>
            </div>
          ) : (
            <pre className="text-xs text-foreground overflow-x-auto whitespace-pre-wrap break-words max-h-[600px] overflow-y-auto">
              {JSON.stringify(compiled, null, 2)}
            </pre>
          )}
        </div>
      </div>

      {/* Parsed JSON Section */}
      <div className="card">
        <div className="p-4 border-b border-accent-cyan/10 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Icon name="FileJson" className="w-5 h-5 text-accent-cyan" />
            <div>
              <h3 className="text-lg font-semibold text-foreground">Parsed Timecard JSON</h3>
              <p className="text-xs text-muted-foreground">Raw parsed data from ICS file</p>
            </div>
          </div>
          <button
            onClick={() => copyToClipboard(parsed, 'parsed')}
            className="flex items-center gap-2 px-4 py-2 bg-accent-cyan/10 hover:bg-accent-cyan/20 text-accent-cyan rounded-lg transition-colors"
          >
            <Icon name={copiedSection === 'parsed' ? 'Check' : 'Copy'} className="w-4 h-4" />
            {copiedSection === 'parsed' ? 'Copied!' : 'Copy JSON'}
          </button>
        </div>
        <div className="p-4 bg-secondary/30">
          <pre className="text-xs text-foreground overflow-x-auto whitespace-pre-wrap break-words max-h-[600px] overflow-y-auto">
            {JSON.stringify(parsed, null, 2)}
          </pre>
        </div>
      </div>

      {/* Identity Section */}
      {parsed.identity && (
        <div className="card">
          <div className="p-4 border-b border-accent-purple/10 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Icon name="User" className="w-5 h-5 text-accent-purple" />
              <h3 className="text-lg font-semibold text-foreground">Identity Data</h3>
            </div>
            <button
              onClick={() => copyToClipboard(parsed.identity, 'identity')}
              className="flex items-center gap-2 px-4 py-2 bg-accent-purple/10 hover:bg-accent-purple/20 text-accent-purple rounded-lg transition-colors"
            >
              <Icon name={copiedSection === 'identity' ? 'Check' : 'Copy'} className="w-4 h-4" />
              {copiedSection === 'identity' ? 'Copied!' : 'Copy'}
            </button>
          </div>
          <div className="p-4 bg-secondary/30">
            <pre className="text-xs text-foreground overflow-x-auto whitespace-pre-wrap">
              {JSON.stringify(parsed.identity, null, 2)}
            </pre>
          </div>
        </div>
      )}

      {/* Pay Summary Section */}
      {parsed.pay_summary && (
        <div className="card">
          <div className="p-4 border-b border-accent-green/10 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Icon name="DollarSign" className="w-5 h-5 text-accent-green" />
              <h3 className="text-lg font-semibold text-foreground">Pay Summary Data</h3>
            </div>
            <button
              onClick={() => copyToClipboard(parsed.pay_summary, 'pay')}
              className="flex items-center gap-2 px-4 py-2 bg-accent-green/10 hover:bg-accent-green/20 text-accent-green rounded-lg transition-colors"
            >
              <Icon name={copiedSection === 'pay' ? 'Check' : 'Copy'} className="w-4 h-4" />
              {copiedSection === 'pay' ? 'Copied!' : 'Copy'}
            </button>
          </div>
          <div className="p-4 bg-secondary/30">
            <pre className="text-xs text-foreground overflow-x-auto whitespace-pre-wrap">
              {JSON.stringify(parsed.pay_summary, null, 2)}
            </pre>
          </div>
        </div>
      )}

      {/* Rotations Sample */}
      {parsed.rotations && parsed.rotations.length > 0 && (
        <div className="card">
          <div className="p-4 border-b border-warning/10 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Icon name="Plane" className="w-5 h-5 text-warning" />
              <h3 className="text-lg font-semibold text-foreground">Rotations Data (First 3)</h3>
            </div>
            <button
              onClick={() => copyToClipboard(parsed.rotations.slice(0, 3), 'rotations')}
              className="flex items-center gap-2 px-4 py-2 bg-warning/10 hover:bg-warning/20 text-warning rounded-lg transition-colors"
            >
              <Icon name={copiedSection === 'rotations' ? 'Check' : 'Copy'} className="w-4 h-4" />
              {copiedSection === 'rotations' ? 'Copied!' : 'Copy'}
            </button>
          </div>
          <div className="p-4 bg-secondary/30">
            <pre className="text-xs text-foreground overflow-x-auto whitespace-pre-wrap max-h-[400px] overflow-y-auto">
              {JSON.stringify(parsed.rotations.slice(0, 3), null, 2)}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimecardRawData;
